import { FormEvent, useEffect, useState } from 'react'
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi } from '@/lib/api/auth.api'
import { createLogger } from '@/lib/logger'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const logger = createLogger('pages/forgot-password')

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sentEmail, setSentEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) {
      setEmail(normalizeEmail(emailParam))
    }
  }, [searchParams])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const normalizedEmail = normalizeEmail(email)

    if (!normalizedEmail) {
      setError('Email is required')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      await authApi.forgotPassword(normalizedEmail)
      setSentEmail(normalizedEmail)
      toast.success('Check your email for a reset code')
    } catch (submitError) {
      logger.error('[ForgotPassword] Request failed:', submitError)
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to send password reset code',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <SplitAuthLayout pageType="forgot-password">
      <div className="w-full">
        {error && (
          <Alert
            variant="destructive"
            className="mb-6 border-destructive/30 bg-destructive/10 text-destructive"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {sentEmail ? (
          <div className="space-y-5">
            <Alert className="border-primary/30 bg-primary/10 text-foreground">
              <AlertDescription>
                We sent a reset code to{' '}
                <span className="font-medium">{sentEmail}</span>.
              </AlertDescription>
            </Alert>
            <Button
              type="button"
              className="h-11 w-full font-medium"
              onClick={() =>
                navigate(
                  `/reset-password?email=${encodeURIComponent(sentEmail)}`,
                )
              }
            >
              Enter Reset Code
            </Button>
            <Button
              type="button"
              variant="ghost"
              animation="none"
              className="w-full"
              onClick={() => setSentEmail(null)}
            >
              Use a different email
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-medium text-foreground"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={event => setEmail(normalizeEmail(event.target.value))}
                className="h-11 border-input bg-background"
                disabled={isLoading}
                required
              />
            </div>

            <Button
              type="submit"
              className="h-11 w-full font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Sending code...' : 'Send Reset Code'}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link
            to={
              email
                ? `/login?email=${encodeURIComponent(normalizeEmail(email))}`
                : '/login'
            }
            className="text-sm font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </SplitAuthLayout>
  )
}
